import { supabase } from './supabase';
import { logger } from './logger';
import { staffService } from './supabaseStaffService';
import { attendeeService } from '../services/attendeeService';

/**
 * Attendee List Service
 * Fetches and formats complete attendee data for printing/downloading
 */

export const attendeeListService = {
  /**
   * Get complete attendee list for a meeting
   * @param {string} meetingId - Meeting UUID
   * @returns {Object} Complete attendee data including customer, sales rep, and staff
   */
  getAttendeeList: async (meetingId) => {
    try {
      logger.debug('Fetching attendee list for meeting', { meetingId });

      // Step 1: Get meeting data
      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .select(`
          id,
          meeting_date,
          start_time,
          end_time,
          restaurant_name,
          restaurant_address,
          attendee_count,
          selected_staff_ids,
          customer_id,
          sales_rep_id
        `)
        .eq('id', meetingId)
        .single();

      if (meetingError) {
        logger.error('Error fetching meeting for attendee list', { error: meetingError.message });
        throw meetingError;
      }

      if (!meeting) {
        throw new Error('Meeting not found');
      }

      // Step 2: Get ALL attendees from meeting_attendees table (physicians + sales rep)
      // For backward compatibility with old meetings, handle errors gracefully
      let physicians = [];
      let salesRepAttendee = null;

      try {
        const attendees = await attendeeService.getMeetingAttendees(meetingId);
        logger.debug('Fetched meeting attendees', { count: attendees.length });

        // Separate attendees by type
        physicians = attendees.filter(a => a.attendee_type === 'physician');
        salesRepAttendee = attendees.find(a => a.attendee_type === 'sales_rep');

        logger.debug('Separated attendees', {
          physicianCount: physicians.length,
          hasSalesRep: !!salesRepAttendee
        });
      } catch (attendeeError) {
        // Old meetings don't have meeting_attendees records - fall back to old customer/sales_rep fields
        logger.warn('Could not load meeting_attendees (likely old meeting), falling back to customer_id/sales_rep_id', {
          error: attendeeError.message,
          meetingId
        });

        // Get customer and sales rep data for backward compatibility
        const { data: meetingWithRefs, error: refError } = await supabase
          .from('meetings')
          .select(`
            customer:customer_id (
              id,
              first_name,
              last_name,
              email,
              phone,
              company,
              specialty,
              title,
              npi_number
            ),
            sales_rep:sales_rep_id (
              id,
              first_name,
              last_name,
              email,
              phone,
              company,
              title,
              territory,
              products
            )
          `)
          .eq('id', meetingId)
          .single();

        if (!refError && meetingWithRefs) {
          // Convert old customer to physician format
          if (meetingWithRefs.customer) {
            physicians = [{
              user_id: meetingWithRefs.customer.id,
              user_profiles: meetingWithRefs.customer,
              is_organizer: true,
              rsvp_status: 'accepted'
            }];
          }

          // Convert old sales_rep to attendee format
          if (meetingWithRefs.sales_rep) {
            salesRepAttendee = {
              user_id: meetingWithRefs.sales_rep.id,
              user_profiles: meetingWithRefs.sales_rep,
              attendee_type: 'sales_rep'
            };
          }
        }
      }

      // Step 3: Get selected staff members
      let staffMembers = [];
      if (meeting.customer_id) {
        try {
          const { data: allStaff, error: staffError } = await supabase
            .from('staff_members')
            .select('id, name, role, email, phone')
            .eq('physician_id', meeting.customer_id)
            .eq('is_active', true);

          if (staffError) {
            logger.warn('Error fetching staff members', { error: staffError.message });
          } else {
            // Filter to only selected staff if selected_staff_ids exists
            if (meeting.selected_staff_ids && meeting.selected_staff_ids.length > 0) {
              staffMembers = (allStaff || []).filter(staff =>
                meeting.selected_staff_ids.includes(staff.id)
              );
              logger.debug('Fetched and filtered staff members', {
                totalStaff: allStaff?.length || 0,
                selectedCount: staffMembers.length
              });
            } else {
              staffMembers = allStaff || [];
            }
          }
        } catch (staffFetchError) {
          logger.warn('Failed to fetch staff members', { error: staffFetchError.message });
        }
      }

      // Step 4: Format attendee data
      const attendeeList = {
        meeting: {
          id: meeting.id,
          date: meeting.meeting_date,
          startTime: meeting.start_time,
          endTime: meeting.end_time,
          restaurant: meeting.restaurant_name,
          restaurantAddress: meeting.restaurant_address,
          attendeeCount: meeting.attendee_count || (physicians.length + (salesRepAttendee ? 1 : 0) + staffMembers.length)
        },
        // All physicians from meeting_attendees
        physicians: physicians.map(p => ({
          id: p.user_id,
          name: `${p.user_profiles?.first_name || ''} ${p.user_profiles?.last_name || ''}`.trim(),
          title: p.user_profiles?.title || p.user_profiles?.specialty || 'Healthcare Provider',
          email: p.user_profiles?.email,
          phone: p.user_profiles?.phone,
          company: p.user_profiles?.company,
          npi: p.user_profiles?.npi_number,
          isOrganizer: p.is_organizer,
          rsvpStatus: p.rsvp_status
        })),
        // Sales rep from meeting_attendees
        salesRep: salesRepAttendee ? {
          id: salesRepAttendee.user_id,
          name: `${salesRepAttendee.user_profiles?.first_name || ''} ${salesRepAttendee.user_profiles?.last_name || ''}`.trim(),
          title: salesRepAttendee.user_profiles?.title || 'Sales Representative',
          email: salesRepAttendee.user_profiles?.email,
          phone: salesRepAttendee.user_profiles?.phone,
          company: salesRepAttendee.user_profiles?.company,
          territory: salesRepAttendee.user_profiles?.territory,
          products: salesRepAttendee.user_profiles?.products
        } : null,
        // Staff members
        staffMembers: staffMembers.map(staff => ({
          id: staff.id,
          name: staff.name,
          role: staff.role || 'Team Member',
          email: staff.email,
          phone: staff.phone
        }))
      };

      logger.debug('Formatted attendee list', {
        meetingId,
        physicianCount: attendeeList.physicians.length,
        hasSalesRep: !!attendeeList.salesRep,
        staffCount: attendeeList.staffMembers.length,
        totalAttendees: attendeeList.physicians.length + (attendeeList.salesRep ? 1 : 0) + attendeeList.staffMembers.length
      });

      return attendeeList;

    } catch (error) {
      logger.error('Error getting attendee list', { error: error.message, meetingId });
      throw error;
    }
  },

  /**
   * Format time for display (converts 24h to 12h format)
   * @param {string} time24 - Time in 24-hour format (HH:MM:SS)
   * @returns {string} Time in 12-hour format (h:MM AM/PM)
   */
  formatTime: (time24) => {
    if (!time24) return '';

    try {
      const [hours, minutes] = time24.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      logger.warn('Error formatting time', { time24, error: error.message });
      return time24;
    }
  },

  /**
   * Format date for display
   * @param {string} dateString - Date string (YYYY-MM-DD)
   * @returns {string} Formatted date (Month DD, YYYY)
   */
  formatDate: (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      logger.warn('Error formatting date', { dateString, error: error.message });
      return dateString;
    }
  }
};

export default attendeeListService;
